import { useState, useEffect } from 'react'

export type ConsentType = 'google' | 'microsoft' | 'instagram'

interface ConsentState {
  [key: string]: boolean | undefined
}

const STORAGE_KEY = 'jcr-cookie-consent'

/**
 * Hook to manage cookie consent preferences
 * Stores consent in localStorage for persistence across page loads
 * GDPR compliant - requires explicit consent before loading third-party content
 */
export const useCookieConsent = () => {
  const [consents, setConsents] = useState<ConsentState>({})
  const [isLoaded, setIsLoaded] = useState(false)

  const getCookieConsents = () => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (error) {
        console.error('Failed to load cookie consent preferences:', error)
      }
    }
    return {};
  }

  // Load consent state from localStorage on mount
  useEffect(() => {
    const consents = getCookieConsents()
    setConsents(consents)
    setIsLoaded(true)
  }, [])

  // Save consent state to localStorage
  const saveConsent = (type: ConsentType | null, value: boolean) => {
    if (typeof window !== 'undefined') {
      try {
        const types = type === null ? ['google', 'microsoft', 'instagram'] : [type]
        let newConsents = { ...getCookieConsents() }
        types.forEach(t => {
          newConsents[t] = value
        })
        setConsents(newConsents)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newConsents))
      } catch (error) {
        console.error('Failed to save cookie consent preference:', error)
      }
    }
  }

  // Check if user has given consent for a specific type
  const hasConsent = (type: ConsentType): boolean => {
    return consents[type] === true
  }

  // Check if user has explicitly rejected consent
  const hasRejected = (type: ConsentType): boolean => {
    return consents[type] === false
  }

  // Check if user has made a decision (either accepted or rejected)
  const hasDecided = (type: ConsentType): boolean => {
    return consents[type] !== undefined
  }

  // Accept consent for a specific type
  const acceptConsent = (type: ConsentType | null) => {
    saveConsent(type, true)
  }

  // Reject consent for a specific type
  const rejectConsent = (type: ConsentType | null) => {
    saveConsent(type, false)
  }

  // Reset consent for a specific type
  const resetConsent = (type: ConsentType) => {
    if (typeof window !== 'undefined') {
      try {
        const newConsents = { ...consents }
        delete newConsents[type]
        setConsents(newConsents)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newConsents))
      } catch (error) {
        console.error('Failed to reset cookie consent preference:', error)
      }
    }
  }

  // Clear all consents
  const clearAllConsents = () => {
    if (typeof window !== 'undefined') {
      try {
        setConsents({})
        localStorage.removeItem(STORAGE_KEY)
      } catch (error) {
        console.error('Failed to clear cookie consent preferences:', error)
      }
    }
  }

  return {
    hasConsent,
    hasRejected,
    hasDecided,
    acceptConsent,
    rejectConsent,
    resetConsent,
    clearAllConsents,
    isLoaded,
  }
}
